// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Soft UI Dashboard PRO React base styles
import typography from "assets/theme/base/typography";
import breakpoints from "assets/theme/base/breakpoints";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useAPI from "../../../../utils/useApi";
import { DASHBOARD, headerIconGradient, sectionHeaderGradient, accentBtnSx } from "../colors";

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Intl.DateTimeFormat("ro-RO", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(raw));
  } catch {
    return raw;
  }
};

function Default({ filterStatus: filterStatusProp, title = "Toate sesizările" }) {
  const { values } = breakpoints;
  const { size } = typography;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const navigate = useNavigate();
  
  const [filterStatus, setFilterStatus] = useState("toate");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("toate");
  const [filterDate, setFilterDate] = useState("");
  const [inProgressSearchTerm, setInProgressSearchTerm] = useState("");
  const [inProgressFilterType, setInProgressFilterType] = useState("toate");
  const [inProgressFilterDate, setInProgressFilterDate] = useState("");
  const [inProgressFilterStatus, setInProgressFilterStatus] = useState("toate");
  const [filterInjuryType, setFilterInjuryType] = useState("toate");
  const [filterResponsibleEntity, setFilterResponsibleEntity] = useState("toate");
  const [inProgressFilterInjuryType, setInProgressFilterInjuryType] = useState("toate");
  const [inProgressFilterResponsibleEntity, setInProgressFilterResponsibleEntity] = useState("toate");
  const [complaintList, setComplaintList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formFetchData, setFormFetchData] = useState(null);

  const listContainerRef = useRef(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageRef = useRef(1);
  const currentFiltersRef = useRef({});

  const client = useAPI();

  const fetchComplaintList = async (params = {}, append = false, pageNumber = null) => {
    loadingRef.current = true;
    setLoading(true);
    try {
      // Use provided pageNumber or fall back to state
      const currentPage = pageNumber !== null ? pageNumber : page;
      
      // Determine status to use: prop > local filter > undefined
      let statusParam = filterStatusProp;
      if (!statusParam || statusParam === "toate") {
        statusParam = filterStatus !== "toate" ? filterStatus : undefined;
      }
      const res = await client.getComplaintFormList({
        _ordering: "-created_at",
        page: currentPage,
        page_size: 12,
        ...(statusParam ? { status: statusParam } : {}),
        ...params,
      });
      setComplaintList(prev =>
        append ? [...prev, ...res.results] : res.results
      );
      const hasNextPage = Boolean(res.next);
      setHasMore(hasNextPage);
      hasMoreRef.current = hasNextPage;
    } catch (error) {
      console.error("Error fetching complaint list:", error);
      // On error, stop trying to fetch more pages
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };


  const fetchFormData = async () => {
    try {
      const response = await client.getComplaintFormData();
      setFormFetchData(response);
    } catch (error) {
      console.error("Error fetching form data:", error);
    }
  };

  // Initial fetch
  useEffect(() => {
    setComplaintList([]); // Reset list on mount or filter change
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;
    loadingRef.current = false;
    currentFiltersRef.current = {}; // Clear filters on mount
    fetchComplaintList({}, false, 1);
    fetchFormData();
    // eslint-disable-next-line
  }, []);

  // Fetch next page when page changes (except for first page)
  useEffect(() => {
    if (page === 1) return;
    // Only fetch if there are more pages available
    if (!hasMoreRef.current) return;
    // Use stored filters for pagination
    fetchComplaintList(currentFiltersRef.current, true, page);
    // eslint-disable-next-line
  }, [page]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Use refs for reliable state checking (avoids stale closure issues)
      if (!listContainerRef.current || loadingRef.current || !hasMoreRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        // Set loading immediately to prevent duplicate scroll triggers
        loadingRef.current = true;
        // Increment pageRef first to prevent multiple increments
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        setPage(nextPage);
      }
    };
    const container = listContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "În așteptare":
        return "warning";
      case "Cerere înregistrată":
        return "info";
      case "Necesită informații suplimentare":
        return "warning";
      case "Redirecționat":
        return "primary";
      case "Cerere soluționată":
        return "success";
      case "Nevalidat":
        return "error";
      default:
        return "default";
    }
  };

  // Helper function to get human-readable labels from values (using ONLY Romanian labels from backend formFetchData)
  const getIncidentTypeLabel = (value) => {
    if (!value) return value;
    
    // If value already looks like a label (contains spaces or Romanian characters), return it as is
    if (value.includes(' ') || /[ăâîșțĂÂÎȘȚ]/.test(value)) {
      return value;
    }
    
    // If formFetchData is not loaded yet, return original value
    if (!formFetchData?.incident_types || !Array.isArray(formFetchData.incident_types)) {
      return value;
    }
    
    // Find exact match in formFetchData.incident_types
    const type = formFetchData.incident_types.find(t => t.value === value);
    
    // Return label from backend if found, otherwise return original value
    return type?.label || value;
  };

  const getInjuryTypeLabel = (value) => {
    if (!value || !formFetchData?.injury_types) return value;
    const type = formFetchData.injury_types.find(t => t.value === value);
    return type ? type.label : value;
  };

  const getResponsibleEntityLabel = (value) => {
    if (!value || !formFetchData?.responsible_entity_types) return value;
    const type = formFetchData.responsible_entity_types.find(t => t.value === value);
    return type ? type.label : value;
  };
  
  const handleViewDetails = (id) => {
    navigate(`/dashboard/detalii-sesizare/${id}`);
  };
  
  const handleSearch = () => {
    const params = {};
    
    if (searchTerm && searchTerm.trim() !== "") {
      params.search = searchTerm.trim();
    }
    if (filterType && filterType !== "toate") {
      params.incident_type = filterType;
    }
    if (filterInjuryType && filterInjuryType !== "toate") {
      params.injury_type = filterInjuryType;
    }
    if (filterResponsibleEntity && filterResponsibleEntity !== "toate") {
      params.responsible_entity_type = filterResponsibleEntity;
    }
    if (filterDate && filterDate !== "") {
      params.created_at = filterDate;
    }
    
    // Store current filters for pagination
    currentFiltersRef.current = params;
    
    // Reset pagination and fetch with filters
    setPage(1);
    pageRef.current = 1;
    setComplaintList([]);
    setHasMore(true);
    hasMoreRef.current = true;
    loadingRef.current = false;
    fetchComplaintList(params, false, 1);
  };

  const renderReportTable = (reports, title, showFilters = false, isInProgress = false) => {
    const searchTermValue = isInProgress ? inProgressSearchTerm : searchTerm;
    const setSearchTermFunc = isInProgress ? setInProgressSearchTerm : setSearchTerm;
    const filterTypeValue = isInProgress ? inProgressFilterType : filterType;
    const setFilterTypeFunc = isInProgress ? setInProgressFilterType : setFilterType;
    const filterDateValue = isInProgress ? inProgressFilterDate : filterDate;
    const setFilterDateFunc = isInProgress ? setInProgressFilterDate : setFilterDate;
    const filterInjuryTypeValue = isInProgress ? inProgressFilterInjuryType : filterInjuryType;
    const setFilterInjuryTypeFunc = isInProgress ? setInProgressFilterInjuryType : setFilterInjuryType;
    const filterResponsibleEntityValue = isInProgress ? inProgressFilterResponsibleEntity : filterResponsibleEntity;
    const setFilterResponsibleEntityFunc = isInProgress ? setInProgressFilterResponsibleEntity : setFilterResponsibleEntity;
    
    return (
      <Card>
        {/* Header */}
        <SoftBox
          px={3}
          pt={3}
          pb={showFilters ? 0 : 2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
            <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftBox
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: headerIconGradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ color: "#fff", fontSize: "18px" }}>assignment</Icon>
            </SoftBox>
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {title}
              </SoftTypography>
              <SoftTypography variant="caption" color="secondary">
                {complaintList.length} sesizări afișate
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        {/* Filter bar */}
        {showFilters && (
          <SoftBox
            mx={3}
            my={2}
            px={2.5}
            py={2}
            sx={{
              background: "#f8fafc",
              borderRadius: "12px",
              border: "1px solid #e8edf2",
            }}
          >
            <SoftBox
              display="flex"
              alignItems="flex-end"
              flexWrap="wrap"
              gap={1.5}
            >
              {/* Căutare text */}
              <SoftBox sx={{ flex: "1 1 180px", minWidth: "150px" }}>
                <SoftTypography variant="caption" fontWeight="medium" color="secondary" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}>
                  Căutare
                </SoftTypography>
                <TextField
                  size="small"
                  placeholder="Nume petent sau cod"
                  value={searchTermValue}
                  onChange={(e) => setSearchTermFunc(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff", fontSize: "0.8rem" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ fontSize: "16px !important", color: "#90a4ae" }}>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </SoftBox>

              {/* Tip incident */}
              <SoftBox sx={{ flex: "1 1 150px", minWidth: "130px" }}>
                <SoftTypography variant="caption" fontWeight="medium" color="secondary" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}>
                  Tip incident
                </SoftTypography>
                <TextField
                  select
                  size="small"
                  value={filterTypeValue}
                  onChange={(e) => setFilterTypeFunc(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff", fontSize: "0.8rem" },
                    "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                  }}
                >
                  <MenuItem value="toate">Toate</MenuItem>
                  {formFetchData?.incident_types?.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </TextField>
              </SoftBox>

              {/* Tip vătămare */}
              <SoftBox sx={{ flex: "1 1 150px", minWidth: "130px" }}>
                <SoftTypography variant="caption" fontWeight="medium" color="secondary" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}>
                  Tip vătămare
                </SoftTypography>
                <TextField
                  select
                  size="small"
                  value={filterInjuryTypeValue}
                  onChange={(e) => setFilterInjuryTypeFunc(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff", fontSize: "0.8rem" },
                    "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                  }}
                >
                  <MenuItem value="toate">Toate</MenuItem>
                  {formFetchData?.injury_types?.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </TextField>
              </SoftBox>

              {/* Responsabil */}
              <SoftBox sx={{ flex: "1 1 150px", minWidth: "130px" }}>
                <SoftTypography variant="caption" fontWeight="medium" color="secondary" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}>
                  Responsabil
                </SoftTypography>
                <TextField
                  select
                  size="small"
                  value={filterResponsibleEntityValue}
                  onChange={(e) => setFilterResponsibleEntityFunc(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff", fontSize: "0.8rem" },
                    "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                  }}
                >
                  <MenuItem value="toate">Toate</MenuItem>
                  {formFetchData?.responsible_entity_types?.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </TextField>
              </SoftBox>

              {/* Status — doar pentru în lucru */}
              {isInProgress && (
                <SoftBox sx={{ flex: "1 1 150px", minWidth: "130px" }}>
                  <SoftTypography variant="caption" fontWeight="medium" color="secondary" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}>
                    Status
                  </SoftTypography>
                  <TextField
                    select
                    size="small"
                    value={inProgressFilterStatus}
                    onChange={(e) => setInProgressFilterStatus(e.target.value)}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff", fontSize: "0.8rem" },
                      "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                    }}
                  >
                    <MenuItem value="toate">Toate</MenuItem>
                    <MenuItem value="În lucru">În lucru</MenuItem>
                    <MenuItem value="Necesită informații suplimentare">Info suplimentare</MenuItem>
                    <MenuItem value="Redirecționat">Redirecționat</MenuItem>
                  </TextField>
                </SoftBox>
              )}

              {/* Dată */}
              <SoftBox sx={{ flex: "1 1 140px", minWidth: "120px" }}>
                <SoftTypography variant="caption" fontWeight="medium" color="secondary" sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}>
                  Dată
                </SoftTypography>
                <TextField
                  type="date"
                  size="small"
                  value={filterDateValue}
                  onChange={(e) => setFilterDateFunc(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff", fontSize: "0.8rem" },
                  }}
                />
              </SoftBox>

              {/* Buton caută */}
              <SoftBox sx={{ flexShrink: 0, alignSelf: "flex-end" }}>
                <SoftButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={handleSearch}
                  sx={{
                    height: "40px",
                    px: 2.5,
                    fontSize: "0.78rem",
                    whiteSpace: "nowrap",
                    ...accentBtnSx,
                  }}
                >
                  <Icon sx={{ mr: 0.5, fontSize: "16px !important" }}>search</Icon>
                  Caută
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        )}

        <SoftBox px={2} pb={2}>
          <Box
            ref={listContainerRef}
            sx={{
              width: '100%',
              overflowY: 'auto',
              maxHeight: '600px', // Set a max height for scrolling
              overflowX: 'auto'
            }}
          >
            <Paper sx={{ 
              width: '100%', 
              minWidth: isMobile ? '800px' : '100%', 
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)',
                bgcolor: 'background.paper',
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                py: 1,
                px: 1.5,
                fontWeight: 'bold'
              }}>
                <SoftTypography variant="caption" fontWeight="bold">Cod</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">Data</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">Petent</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">Tip</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">Status</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">Instituție</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">Acțiuni</SoftTypography>
              </Box>
              {reports.map((report) => (
                <Box 
                  key={report.uuid}
                  sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                    py: 1,
                    px: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <SoftTypography variant="caption" fontWeight="medium">{report.uuid}</SoftTypography>
                  <SoftTypography variant="caption">{formatDate(report.created_at)}</SoftTypography>
                  <SoftTypography variant="caption">{report.name}</SoftTypography>
                  <SoftTypography variant="caption">
                    {report.incident_type_label || report.incident_type_display || getIncidentTypeLabel(report.incident_type)}
                  </SoftTypography>
                  <Box>
                    <Chip
                      size="small"
                      label={report.status || 'Necunoscut'}
                      color={getStatusColor(report.status)}
                      sx={{ 
                        fontSize: '0.65rem', 
                        height: '20px',
                        '& .MuiChip-label': { 
                          px: 1,
                          py: 0.1
                        }
                      }}
                      variant="outlined"
                    />
                  </Box>
                  <SoftTypography variant="caption">{report.institution_entity}</SoftTypography>
                  <Box>
                    <SoftButton 
                      variant="text" 
                      color="info" 
                      size="medium" 
                      sx={{ py: 0.25, px: 1, minWidth: 'auto' }}
                      onClick={() => handleViewDetails(report.uuid)}
                    >
                      <Icon fontSize="medium">visibility</Icon>
                    </SoftButton>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        </SoftBox>
        {loading && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <SoftTypography variant="caption">Se încarcă...</SoftTypography>
          </Box>
        )}
        {!hasMore && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <SoftTypography variant="caption" color="text">
              Nu mai sunt rezultate.
            </SoftTypography>
          </Box>
        )}
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
        <SoftBox mb={2}>
          {renderReportTable(complaintList, title, true)}
        </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
